import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { User as UserIcon, Phone, Mail, ArrowLeft, CheckCircle2, KeyRound, Bell } from 'lucide-react';
import api from '@/lib/api';

// Phase 13-후속 · Profile Phone Editor.
// Members set/edit name and phone once — downstream Checkout and Inquiry
// modals auto-fill from /auth/me and the '연락처를 입력해주세요' loop ends.

const providerLabel = (u) =>
  u?.provider === 'kakao' ? 'Kakao 계정'
  : u?.provider === 'google' || u?.google_id || (u?.email && !u?.provider) ? 'Google 계정'
  : 'LIVEMOA 계정';

const isValidPhone = (v) => {
  const d = (v || '').replace(/\D+/g, '');
  return d.length >= 9 && d.length <= 11;
};

export default function Profile() {
  const nav = useNavigate();
  const location = useLocation();
  const [me, setMe] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '' });
  const [saving, setSaving] = useState(false);
  // STEP KAKAO-RECIPIENT-P1 · marketing/혜택 알림 수신동의 (Global opt-in)
  const [marketingConsent, setMarketingConsent] = useState(null);
  const [savingConsent, setSavingConsent] = useState(false);
  const params = new URLSearchParams(location.search);
  const returnUrl = params.get('return_url') || null;

  useEffect(() => {
    api.get('/auth/me')
      .then((r) => {
        setMe(r.data);
        setForm({ name: r.data.name || '', phone: r.data.phone || '' });
      })
      .catch(() => {
        const back = encodeURIComponent(location.pathname + location.search);
        nav(`/login?return_url=${back}`, { replace: true });
      });
    api.get('/me/marketing-consent')
      .then((r) => setMarketingConsent(r.data))
      .catch(() => setMarketingConsent(null));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleMarketingConsent = async () => {
    if (savingConsent || !marketingConsent) return;
    const next = !marketingConsent.enabled;
    setSavingConsent(true);
    try {
      const r = await api.patch('/me/marketing-consent', {
        enabled: next, source: 'consumer_notification_settings',
      });
      setMarketingConsent(r.data);
      toast.success(next ? '혜택·이벤트 알림 ON' : '혜택·이벤트 알림 OFF');
    } catch (e) {
      toast.error(e?.response?.data?.detail || '저장 실패');
    } finally { setSavingConsent(false); }
  };

  const save = async () => {
    if (!form.name.trim() && !form.phone.trim()) {
      toast.error('이름 또는 연락처 중 하나 이상 입력해주세요.'); return;
    }
    if (form.phone && !isValidPhone(form.phone)) {
      toast.error('휴대폰 번호를 올바르게 입력해주세요.'); return;
    }
    setSaving(true);
    try {
      const r = await api.patch('/auth/profile', {
        name: form.name.trim() || undefined,
        phone: form.phone.trim() || undefined,
      });
      setMe(r.data);
      setForm({ name: r.data.name || '', phone: r.data.phone || '' });
      toast.success('회원정보가 저장되었습니다.');
      if (returnUrl && returnUrl.startsWith('/') && !returnUrl.startsWith('//')) {
        setTimeout(() => nav(returnUrl), 350);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || '저장 실패');
    } finally { setSaving(false); }
  };

  if (!me) {
    return <div className="p-8 text-center text-xs text-gray-500">불러오는 중…</div>;
  }

  const phoneOk = !!(me.phone && isValidPhone(me.phone));

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0A0A]">
      <div className="max-w-md mx-auto p-4 pb-16" data-testid="profile-page">
        <button onClick={() => nav(-1)} className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-black dark:hover:text-white pt-2 pb-4" data-testid="profile-back">
          <ArrowLeft size={14} /> 뒤로
        </button>

        <div className="flex items-center gap-2 mb-1">
          <UserIcon size={16} className="text-[#FF3B30]" />
          <h1 className="text-xl font-black">회원정보</h1>
        </div>
        <p className="text-[11px] text-gray-500 mb-5">
          {providerLabel(me)} 로 가입 · 회원 ID <span className="font-mono">{me.user_id}</span>
        </p>

        {returnUrl && (
          <div className="mb-4 p-3 rounded-xl border border-[#FF3B30]/30 bg-[#FF3B30]/5 text-[11px]" data-testid="profile-return-hint">
            연락처 저장 후 원래 진행하시던 페이지로 자동 복귀합니다.
          </div>
        )}

        <div className="space-y-4">
          {/* Read-only email · comes from OAuth provider */}
          <div className="p-3 rounded-2xl border border-black/10 dark:border-white/10">
            <label className="text-[10px] font-black text-gray-500 inline-flex items-center gap-1"><Mail size={11} /> 이메일 (수정 불가)</label>
            <p className="mt-1 text-sm font-bold" data-testid="profile-email">{me.email || '연결된 이메일 없음'}</p>
            <p className="text-[10px] text-gray-400 mt-0.5">OAuth 로그인 정보에서 자동 가져옵니다.</p>
          </div>

          {/* Name */}
          <div className="p-3 rounded-2xl border border-black/10 dark:border-white/10">
            <label className="text-[10px] font-black text-gray-500 inline-flex items-center gap-1"><UserIcon size={11} /> 이름 / 닉네임</label>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="주문서·문의에 표시될 이름"
              maxLength={40}
              data-testid="profile-input-name"
              className="mt-1 w-full px-3 py-2 rounded-lg border border-black/10 dark:border-white/10 text-sm bg-transparent focus:outline-none focus:border-[#FF3B30]"
            />
            <p className="text-[10px] text-gray-400 mt-1">비워두면 카카오 닉네임을 그대로 사용합니다.</p>
          </div>

          {/* Phone — the whole point of this screen */}
          <div className={`p-3 rounded-2xl border ${phoneOk ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-amber-500/30 bg-amber-500/5'}`}>
            <label className="text-[10px] font-black text-gray-500 inline-flex items-center gap-1"><Phone size={11} /> 휴대폰 번호</label>
            <input
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="예) 010-1234-5678"
              inputMode="tel"
              maxLength={13}
              data-testid="profile-input-phone"
              className="mt-1 w-full px-3 py-2 rounded-lg border border-black/10 dark:border-white/10 text-sm bg-transparent focus:outline-none focus:border-[#FF3B30]"
            />
            <p className="text-[10px] mt-1 inline-flex items-center gap-1 text-gray-500">
              {phoneOk
                ? <><CheckCircle2 size={11} className="text-emerald-500" /> 저장된 번호로 주문·문의에 자동 채워집니다.</>
                : <><KeyRound size={11} className="text-amber-500" /> 등록해두면 다음 구매·문의부터 원클릭으로 이어집니다.</>
              }
            </p>
          </div>

          {/* STEP KAKAO-RECIPIENT-P1 · Marketing/혜택 알림 수신동의 (Global opt-in) */}
          <div className="p-3 rounded-2xl border border-black/10 dark:border-white/10"
               data-testid="profile-marketing-consent-section">
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-black text-black dark:text-white inline-flex items-center gap-1">
                  <Bell size={12} className="text-[#FF3B30]" /> 혜택 및 이벤트 알림 받기
                </p>
                <p className="text-[10px] text-gray-500 mt-0.5">
                  팔로우한 판매자의 방송, 이벤트 및 혜택 소식을 받을 수 있어요.
                </p>
              </div>
              <button
                type="button"
                onClick={toggleMarketingConsent}
                disabled={savingConsent || !marketingConsent}
                data-testid="profile-marketing-consent-toggle"
                className={`shrink-0 w-14 h-7 rounded-full transition-colors relative ${
                  marketingConsent?.enabled ? 'bg-[#FF3B30]' : 'bg-gray-300 dark:bg-white/15'
                } disabled:opacity-40`}
                aria-pressed={marketingConsent?.enabled === true}
              >
                <span
                  className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-all ${
                    marketingConsent?.enabled ? 'left-7' : 'left-0.5'
                  }`}
                />
              </button>
            </div>
            <p className="text-[9px] text-gray-400 mt-2 leading-relaxed">
              ※ 주문·결제·배송 등 거래 안내는 이 설정과 무관하게 계속 전송됩니다.
              동의 시각과 철회 시각을 함께 기록합니다.
            </p>
          </div>

          <button
            onClick={save}
            disabled={saving}
            data-testid="profile-save-btn"
            className="w-full py-3 rounded-full bg-[#FF3B30] text-white text-sm font-black disabled:opacity-40"
          >
            {saving ? '저장 중…' : '회원정보 저장'}
          </button>
        </div>
      </div>
    </div>
  );
}
